/**
 * Frontend Tool Handlers — 文件管理/批量操作类
 *
 * 处理的工具：
 * - manage_workspace_files → 批量文件系统操作
 * - save_file_to_destination → 等待上游文件生成
 * - transfer_local_to_cloud → 本地→云端传输
 */

import {
  registerFrontendTool,
  authedPost,
  type FrontendToolContext,
} from '../frontend-dispatcher';
import {
  renameFileByPath,
  createFolderAndMove,
  deleteFileByPath,
  readFileAsBase64,
} from '../../hooks/useFileSystem';

// ─── manage_workspace_files ─────────────────
registerFrontendTool('manage_workspace_files', async (args, ctx) => {
  if (!args.operations || !Array.isArray(args.operations)) {
    return `[Error: Missing operations argument]`;
  }

  const results: string[] = [];
  for (const op of args.operations) {
    try {
      if (op.source !== 'local') {
        results.push(`⏳ ${op.action} on case system: pending (cloud ops not yet implemented)`);
        continue;
      }
      if (op.action === 'rename' && op.newName) {
        const r = await renameFileByPath(ctx.localDirHandle, op.path, op.newName);
        results.push(r.success ? `✅ Renamed ${op.path} → ${op.newName}` : `❌ Rename ${op.path}: ${r.error}`);
      } else if (op.action === 'delete') {
        const fileName = op.path.split('/').pop() || op.path;
        const r = await deleteFileByPath(ctx.localDirHandle, op.path, fileName);
        results.push(r.success ? `✅ Deleted ${op.path}` : `❌ Delete ${op.path}: ${r.error}`);
      } else if (op.action === 'create_folder') {
        const r = await createFolderAndMove(ctx.localDirHandle, op.path, []);
        results.push(r.success ? `✅ Created folder ${op.path}` : `❌ Create folder ${op.path}: ${r.errors.join('; ')}`);
      } else if (op.action === 'move' && op.destination) {
        const r = await createFolderAndMove(ctx.localDirHandle, op.destination, [op.path]);
        results.push(r.success ? `✅ Moved ${op.path} → ${op.destination}` : `❌ Move ${op.path}: ${r.errors.join('; ')}`);
      } else {
        results.push(`⚠️ Unknown action: ${op.action}`);
      }
    } catch (e: any) {
      results.push(`❌ ${op.action} ${op.path}: ${e?.message || e}`);
    }
  }

  await ctx.refreshFileTree();
  return `[Workspace整理完成 (${args.operations.length} 个操作)]:\n${results.join('\n')}\n\n本地文件树已更新。`;
});

// ─── save_file_to_destination ───────────────
registerFrontendTool('save_file_to_destination', async (args) => {
  return `[Info: save_file_to_destination 已收到保存指令 (${args.fileName} → ${args.destination})。文件将在审查/起草完成后自动保存。]`;
});

// ─── transfer_local_to_cloud ────────────────
registerFrontendTool('transfer_local_to_cloud', async (args, ctx) => {
  if (!args.path || !args.destination) {
    return `[Error: Missing path or destination argument]`;
  }

  ctx.setMessageStatus('searching', `☁️ 正在上传 ${args.path} 到 ${args.destination}...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.path);
  if (!fileData) {
    return `[Error: Failed to read local file ${args.path}]`;
  }

  const uploadResp = await authedPost(
    `${ctx.apiBaseUrl}/api/cloud/${args.destination}/upload-buffer`,
    {
      fileName: fileData.fileName,
      base64: fileData.base64,
      mimeType: fileData.mimeType,
      folderId: args.destinationFolderId,
    },
    ctx.authToken,
  );

  if (uploadResp.ok) {
    const result = await uploadResp.json();
    return `[Success: "${fileData.fileName}" uploaded to ${args.destination}${result.webUrl ? ` (${result.webUrl})` : ''}]`;
  }
  return `[Error: Upload to ${args.destination} failed: ${uploadResp.statusText}]`;
});
